import { useTexture } from '@react-three/drei';
import { ThreeEvent } from '@react-three/fiber';
import {
  FootprintPolygonsType,
  GridObjectType,
  GridSpaceType
} from 'data/types';
import { use3DRightClick } from 'hooks/use-3d-right-click';
import { DirectPointDragHandlers } from 'hooks/use-direct-point-drag';
import { useMemo } from 'react';
import { Color, MeshStandardMaterial, RepeatWrapping } from 'three';
import {
  CONDENSER_3D_DIMENSIONS,
  getCondenser3DPlacement
} from 'utils/3d-equipment';
import {
  getOpacityMaterialProps,
  getOpacityRenderOrder
} from 'utils/3d-opacity';
import { EMPTY_FOOTPRINT_POLYGONS } from 'utils/footprint-polygons';
import condenserSideTexture from './assets/condenser-side.jpg';
import condenserTopTexture from './assets/condenser-top.jpg';

type Condenser3DProps = Readonly<{
  object: GridObjectType;
  spaces?: GridSpaceType[];
  footprints?: FootprintPolygonsType;
  yOffset?: number;
  opacity?: number;
  selected?: boolean;
  onClick?: (event: ThreeEvent<MouseEvent>) => void;
  onRightClick?: (event: ThreeEvent<PointerEvent>) => void;
  dragHandlers?: DirectPointDragHandlers;
}>;

export default function Condenser3D({
  object,
  spaces = [],
  footprints = EMPTY_FOOTPRINT_POLYGONS,
  yOffset = 0,
  opacity = 1,
  selected = false,
  onClick,
  onRightClick,
  dragHandlers
}: Condenser3DProps) {
  const rightClick = use3DRightClick(onRightClick);
  const [sideTexture, topTexture] = useTexture([
    condenserSideTexture,
    condenserTopTexture
  ]);

  const { width, height, depth } = CONDENSER_3D_DIMENSIONS;

  const placement = useMemo(
    () => getCondenser3DPlacement(object, spaces, footprints),
    [object, spaces, footprints]
  );

  const materials = useMemo(() => {
    const createMaterial = (
      texture: typeof sideTexture,
      repeatX: number,
      repeatY: number
    ) => {
      const texClone = texture.clone();
      texClone.wrapS = RepeatWrapping;
      texClone.wrapT = RepeatWrapping;
      texClone.repeat.set(repeatX, repeatY);
      texClone.needsUpdate = true;
      return new MeshStandardMaterial({
        map: texClone,
        emissive: selected ? new Color('#2563eb') : new Color('#000000'),
        emissiveIntensity: selected ? 0.35 : 0,
        ...getOpacityMaterialProps(opacity)
      });
    };

    return [
      createMaterial(topTexture, 1, 1),
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1),
      createMaterial(sideTexture, 1, 1)
    ];
  }, [sideTexture, topTexture, opacity, selected]);

  return (
    <group
      position={[placement.x, placement.y + yOffset, placement.z]}
      rotation={[0, placement.rotation, 0]}
    >
      <mesh
        material={materials}
        renderOrder={getOpacityRenderOrder(opacity)}
        onClick={onClick}
        onPointerDown={(event) => {
          rightClick.onPointerDown(event);
          dragHandlers?.onPointerDown(event);
        }}
        onPointerMove={dragHandlers?.onPointerMove}
        onPointerUp={(event) => {
          rightClick.onPointerUp(event);
          dragHandlers?.onPointerUp(event);
        }}
        onPointerCancel={dragHandlers?.onPointerCancel}
        onPointerOver={
          dragHandlers
            ? (event) => {
                event.stopPropagation();
                document.body.style.cursor = 'grab';
              }
            : undefined
        }
        onPointerOut={
          dragHandlers
            ? () => {
                document.body.style.cursor = '';
              }
            : undefined
        }
      >
        <boxGeometry args={[width, height, depth]} />
      </mesh>
    </group>
  );
}
